import { Loader2, Lock, MoreHorizontal, Pencil, UserMinus } from "lucide-react";
import { IAM_TAB, getTabDef } from "../../lib/tab-registry";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { TableCell, TableRow } from "../ui/table";
import { ACTIVE_PILL, DESTRUCTIVE_PILL } from "../users-config/trial-pills";
import type { AdminAccount } from "./iam-api";

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "-";

type AdminTableRowProps = {
  admin: AdminAccount;
  isMe: boolean;
  isActionLoading: boolean;
  onEditTabs: (admin: AdminAccount) => void;
  onDemote: (admin: AdminAccount) => void;
};

/** One admin row, laid out like a Users Config row so the two tabs match. */
export const AdminTableRow = ({ admin, isMe, isActionLoading, onEditTabs, onDemote }: AdminTableRowProps) => {
  const banned = admin.banned;
  return (
    <TableRow className={`group ${banned ? "bg-destructive/5" : "hover:bg-muted/40"}`}>
      <TableCell
        className={`sticky left-0 z-10 border-r border-border/60 px-4 py-3 whitespace-normal shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] ${banned ? "bg-destructive/5" : "bg-card group-hover:bg-muted/40"}`}
      >
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="bg-lime-600/10 text-lime-900 font-medium">
              {(admin.name || admin.email || "A").charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-foreground">
              {admin.name || "Unnamed"}
              {isMe ? <span className="ml-2 text-xs font-normal text-muted-foreground">(you)</span> : null}
            </span>
            <span className="text-xs text-muted-foreground">{admin.email}</span>
          </div>
        </div>
      </TableCell>
      <TableCell className="px-4 py-3">
        {admin.username ? (
          <span className="text-sm text-muted-foreground">@{admin.username}</span>
        ) : (
          <span className="text-xs text-muted-foreground/50">-</span>
        )}
      </TableCell>
      <TableCell className="px-4 py-3 whitespace-normal">
        {admin.permissions.length === 0 ? (
          <span className="text-xs text-muted-foreground">No tabs</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {admin.permissions.map((tab) => (
              <Badge
                key={tab}
                variant="outline"
                className={`text-xs ${tab === IAM_TAB ? "border-lime-600/20 bg-lime-600/10 text-lime-900" : "border-border/60 bg-muted/30 text-foreground"}`}
              >
                {getTabDef(tab).label}
              </Badge>
            ))}
          </div>
        )}
      </TableCell>
      <TableCell className="px-4 py-3">
        <div className="flex flex-col gap-0.5">
          <Badge className={`gap-1 ${banned ? DESTRUCTIVE_PILL : ACTIVE_PILL}`}>
            {banned ? <Lock className="h-3 w-3" strokeWidth={2.5} /> : <span className="h-1.5 w-1.5 rounded-full bg-current" />}
            {banned ? "Banned" : "Active"}
          </Badge>
          {admin.permissions.length === 0 ? (
            <span className="text-[11px] text-muted-foreground">Cannot open the dashboard</span>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="px-4 py-3 text-sm text-muted-foreground">
        <div className="flex flex-col">
          <span>{formatDate(admin.lastChangedAt)}</span>
          {admin.lastChangedBy ? <span className="text-[11px]">by #{admin.lastChangedBy}</span> : null}
        </div>
      </TableCell>
      <TableCell className="px-4 py-3 text-right">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" disabled={isActionLoading}>
              {isActionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onEditTabs(admin)} className="gap-2">
              <Pencil className="h-4 w-4" />
              Edit tabs
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDemote(admin)}
              disabled={isMe}
              className="gap-2 text-destructive focus:text-destructive"
            >
              <UserMinus className="h-4 w-4" />
              {isMe ? "Cannot demote yourself" : "Demote to regular user"}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};
